// src/pages/Appli/AppliAdmin/AlbumsPhotos/ManagePhotoModal.jsx
import React, { useMemo, useRef, useState } from "react";
import { FaTimes, FaPlus, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";

import Button from "../../../../components/Button/Button";
import { addPhoto, deletePhoto, uploadImage } from "../../../../services/photoServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const toAbs = (url) => {
  if (!url) return "";
  return isExternalURL(url) ? url : `${BASE_URL}${url}`;
};

const ManagePhotoModal = ({ album, onClose, onUpdated }) => {
  const [photos, setPhotos] = useState(Array.isArray(album?.photos) ? album.photos : []);
  const [newPhotoUrl, setNewPhotoUrl] = useState("");
  const [newPhotoDescription, setNewPhotoDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fileRef = useRef(null);

  const isDemoMode = useMemo(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    return !!(user && user.role_id === 4);
  }, []);

  const guardDemo = () => {
    if (isDemoMode) {
      toast.info("Fonction désactivée en mode Démonstration");
      return true;
    }
    return false;
  };

  const resetInputs = () => {
    setNewPhotoUrl("");
    setNewPhotoDescription("");
    setImageFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleAddPhoto = async () => {
    if (guardDemo()) return;

    if (!newPhotoUrl && !imageFile) {
      toast.error("Ajoute une URL ou sélectionne un fichier.");
      return;
    }

    setIsLoading(true);

    try {
      let uploadedPhotoUrl = newPhotoUrl;

      if (imageFile) {
        const fd = new FormData();
        fd.append("image", imageFile);
        fd.append("category", "photos");

        const uploadResponse = await uploadImage(fd);
        if (!uploadResponse?.filePath) throw new Error("Réponse upload invalide");
        uploadedPhotoUrl = uploadResponse.filePath;
      }

      const payload = {
        gallerie_id: album.id,
        photo_url: uploadedPhotoUrl,
        description: newPhotoDescription,
        is_active: true,
      };

      const addedPhoto = await addPhoto(payload);

      setPhotos((prev) => [...prev, addedPhoto]);
      resetInputs();
      toast.success("Photo ajoutée ✅");
      onUpdated?.();
    } catch (error) {
      console.error("Erreur ajout photo :", error);
      toast.error("Erreur lors de l’ajout de la photo.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (guardDemo()) return;
    if (!window.confirm("Supprimer cette photo ?")) return;

    setIsLoading(true);
    try {
      await deletePhoto(photoId);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      toast.success("Photo supprimée ✅");
      onUpdated?.();
    } catch (error) {
      console.error("Erreur suppression photo :", error);
      toast.error("Erreur lors de la suppression.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl rounded-3xl border border-white/10 bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
        <div className="px-6 py-4 bg-primary text-white flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-extrabold truncate">
              Photos — {album?.titre || "Album"}
            </h2>
            <p className="text-xs opacity-90">
              {photos.length} photo(s)
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl px-3 py-2 bg-white/15 hover:bg-white/20 transition"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
          {/* Grid photos */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group rounded-2xl overflow-hidden border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5">
                <div className="aspect-square">
                  <img
                    src={toAbs(photo.photo_url)}
                    alt={photo.description || "photo"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => handleDeletePhoto(photo.id)}
                  disabled={isLoading}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition rounded-full p-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                  title="Supprimer"
                >
                  <FaTrash />
                </button>

                {photo.description ? (
                  <div className="px-2 py-2 text-xs text-gray-700 dark:text-gray-200 line-clamp-2">
                    {photo.description}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          {!photos.length && (
            <div className="text-sm text-gray-600 dark:text-gray-300 text-center">
              Aucune photo pour le moment.
            </div>
          )}

          {/* Add photo */}
          <div className="rounded-3xl border border-black/10 dark:border-white/10 bg-white/80 dark:bg-black/20 p-5">
            <h3 className="text-sm font-extrabold text-gray-900 dark:text-white">
              Ajouter une photo
            </h3>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  Fichier (recommandé)
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                  disabled={isLoading}
                  className="mt-1 w-full p-2 border border-gray-300 dark:border-gray-700 rounded-2xl dark:bg-gray-800 dark:text-gray-200"
                />
              </div>

              <div className="md:col-span-1">
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  URL (optionnel)
                </label>
                <input
                  type="text"
                  value={newPhotoUrl}
                  onChange={(e) => setNewPhotoUrl(e.target.value)}
                  disabled={isLoading}
                  className="mt-1 w-full p-2 border border-gray-300 dark:border-gray-700 rounded-2xl dark:bg-gray-800 dark:text-gray-200"
                  placeholder="https://..."
                />
              </div>

              <div className="md:col-span-1">
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  Description
                </label>
                <input
                  type="text"
                  value={newPhotoDescription}
                  onChange={(e) => setNewPhotoDescription(e.target.value)}
                  disabled={isLoading}
                  className="mt-1 w-full p-2 border border-gray-300 dark:border-gray-700 rounded-2xl dark:bg-gray-800 dark:text-gray-200"
                  placeholder="Ex: Coupe du club 2026"
                />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <Button
                text="Ajouter"
                onClick={handleAddPhoto}
                icon={FaPlus}
                disabled={isLoading}
                className="gap-2"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-black/10 dark:border-white/10 flex justify-end">
          <Button text="Fermer" onClick={onClose} icon={FaTimes} className="gap-2" />
        </div>
      </div>
    </div>
  );
};

export default ManagePhotoModal;
